import type { Reference } from './contracts';

/*
 * Tags in the prompt (Tarek, 14 Oct: "tag added references and elements inside my prompt bar").
 * The prompt is kept as text with one token per pill — `@[ref:<id>:<name>]`, both parts
 * URI-encoded — so a run brought back from the canvas keeps its pills, and an id with no
 * reference behind it any more still reads as its name. On the way to the model each token
 * becomes the words that model reads (ai-back prompt-guides `mentions`). A model has no idea
 * what an element is — it gets pictures (Tarek, 14 Oct: "i'm sending for example the character
 * sheet … it's always an image"), so an element is named WITH its pictures' numbers, "Tarek
 * (images 1–3)", matching drive-back's element block ("Tarek — character (image 1, 2, 3)"); a
 * picture, clip or sound by its NUMBER among what the run sends: "image 3" for most
 * models, `@Image3` / `@Video1` / `@Audio1` for the reference endpoints that read tags
 * (Seedance 2.5, Kling O1). Element pictures are numbered first: nodes-back puts them ahead
 * of the run's own pictures (`mergeReferenceImages`).
 */

const TOKEN = /@\[ref:([^:\]]*):([^\]]*)\]/g;
/** The reference endpoints that read `@Image1` tags (ai-back prompt-guides AT_TAGS). */
const AT_TAGS = /seedance-2\.5|kling-o1/i;

export type PromptPart = { t: 'text'; v: string } | { t: 'ref'; id: string; name: string };

export const refToken = (ref: Pick<Reference, 'id' | 'name'>) => `@[ref:${encodeURIComponent(ref.id)}:${encodeURIComponent(ref.name)}]`;

const decode = (value: string) => { try { return decodeURIComponent(value); } catch { return value; } };

/** The prompt as text runs and tags, in order. */
export function promptParts(text: string): PromptPart[] {
  const parts: PromptPart[] = [];
  let at = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index! > at) parts.push({ t: 'text', v: text.slice(at, match.index) });
    parts.push({ t: 'ref', id: decode(match[1]!), name: decode(match[2]!) });
    at = match.index! + match[0].length;
  }
  if (at < text.length) parts.push({ t: 'text', v: text.slice(at) });
  return parts;
}

/** Every tag of reference `id` pointed at `ref` instead (an element whose pictures were re-picked). */
export const retagPrompt = (text: string, id: string, ref: Pick<Reference, 'id' | 'name'>) =>
  text.replace(TOKEN, (token, rawId: string) => decode(rawId) === id ? refToken(ref) : token);

/** The prompt as a person reads it: each tag as `@Name`. */
export const promptReadable = (text: string) => promptParts(text).map(part => part.t === 'text' ? part.v : `@${part.name}`).join('');

const kindOf = (ref: Reference) => ref.role === 'first' || ref.role === 'last' ? ref.role : ref.role === 'video' || ref.mediaKind === 'video' ? 'video' : ref.role === 'audio' || ref.mediaKind === 'audio' ? 'audio' : 'image';

/** The words the model is sent: every tag named the way `modelId` reads its inputs. */
/** `elementPictures`: how many pictures each element sends now (drive-back's resolve), when known. */
export function promptForModel(text: string, references: Reference[], modelId: string, elementPictures?: Map<string, number>): string {
  if (!text.includes('@[ref:')) return text;
  const tags = AT_TAGS.test(modelId);
  // An element is its pictures: numbered first, in the order the elements are sent.
  const elementNumbers = new Map<string, number[]>();
  let next = 0;
  for (const ref of references) if (ref.element) elementNumbers.set(ref.id, Array.from({ length: elementPictures?.get(ref.id) ?? Math.max(1, ref.elementImageCount ?? 1) }, () => ++next));
  const numbers = new Map<string, number>();
  const seen = { image: next, video: 0, audio: 0 };
  const image = (n: number) => tags ? `@Image${n}` : `image ${n}`;
  for (const ref of references) {
    const kind = kindOf(ref);
    if (ref.element || kind === 'first' || kind === 'last') continue;
    numbers.set(ref.id, ++seen[kind]);
  }
  return promptParts(text).map(part => {
    if (part.t === 'text') return part.v;
    const ref = references.find(item => item.id === part.id);
    if (!ref) return part.name;
    if (ref.element) {
      const list = elementNumbers.get(ref.id)!;
      if (!list.length) return ref.name;
      const pictures = tags ? list.map(image).join(' ') : list.length === 1 ? image(list[0]!) : `images ${list[0]}–${list.at(-1)}`;
      return `${ref.name} (${pictures})`;
    }
    const kind = kindOf(ref);
    if (kind === 'first') return 'the first frame';
    if (kind === 'last') return 'the last frame';
    const n = numbers.get(ref.id)!;
    return tags ? `@${kind[0]!.toUpperCase()}${kind.slice(1)}${n}` : `${kind} ${n}`;
  }).join('')
    // A tag is followed by a space so the caret has somewhere to land; punctuation typed after it stays tidy.
    .replace(/(\)|@(?:Image|Video|Audio)\d+|(?:image|video|audio) \d+|frame) +([,.;:!?])/g, '$1$2');
}
